import { useMemo } from 'react';
import { matchPath, NavLink, useLocation } from 'react-router-dom';
import { useVisibleAppsByClassification } from '@/lib/applications/use-visible-apps-by-classification';
import type { WorkspaceClientId } from './nav.config';
import { ROUTES } from '@/router/routes';
import { useAuth } from '@/lib/auth/auth-context';
import { ApplicationClassificationBar } from './ApplicationClassificationBar';
import brandLogo from '../../../../../assets/favicon/logo_light_horizontal.png';

interface BreadcrumbRoute {
  readonly path: string;
  readonly crumbs: readonly string[];
  readonly end?: boolean;
}

const BREADCRUMB_ROUTES: readonly BreadcrumbRoute[] = [
  { path: ROUTES.DASHBOARD, crumbs: ['Dashboard'] },
  { path: ROUTES.INTEGRATION_DASHBOARD, crumbs: ['OSS Integration', 'Home'] },
  { path: `${ROUTES.INTEGRATION_DOCUMENTS}/:id`, crumbs: ['OSS Integration', 'Messages', 'Message Detail'] },
  { path: ROUTES.INTEGRATION_DOCUMENTS, crumbs: ['OSS Integration', 'Messages'] },
  { path: ROUTES.INTEGRATION_GENERATE, crumbs: ['OSS Integration', 'Generate Document'] },
  { path: ROUTES.INTEGRATION_OPERATION_FLOWS, crumbs: ['OSS Integration', 'Flows'] },
  { path: ROUTES.INTEGRATION_FLOW_BUILDER, crumbs: ['OSS Integration', 'Set up Integration'] },
  { path: ROUTES.INTEGRATION_FLOWS, crumbs: ['OSS Integration', 'Flows'] },
  { path: ROUTES.INTEGRATION_EXECUTIONS, crumbs: ['OSS Integration', 'Monitoring'] },
  { path: ROUTES.INTEGRATION_DLQ, crumbs: ['OSS Integration', 'Recovery'] },
  { path: `${ROUTES.INTEGRATION_EXCHANGES}/:id`, crumbs: ['OSS Integration', 'Exchange Log', 'Exchange Detail'] },
  { path: ROUTES.INTEGRATION_EXCHANGES, crumbs: ['OSS Integration', 'Exchange Log'] },
  { path: ROUTES.INTEGRATION_COMMAND_CENTER, crumbs: ['OSS Integration', 'Command Center'] },
  { path: ROUTES.INTEGRATION_MARITIME, crumbs: ['OSS Integration', 'Maritime'] },
  { path: ROUTES.INTEGRATION_SCHEMAS, crumbs: ['OSS Integration', 'Schemas'] },
  { path: ROUTES.INTEGRATION_MAPPING_DESIGNER, crumbs: ['OSS Integration', 'Mapping Designer'] },
  { path: ROUTES.INTEGRATION_TEST_LAB, crumbs: ['OSS Integration', 'Test Lab'] },
  { path: ROUTES.INTEGRATION_CONNECTOR_CONSOLE, crumbs: ['OSS Integration', 'Delivery Channels'] },
  { path: ROUTES.INTEGRATION_CONNECTORS, crumbs: ['OSS Integration', 'Technical Catalog'] },
  { path: ROUTES.INTEGRATION_CONNECTOR_INSTANCES, crumbs: ['OSS Integration', 'Delivery Endpoints'] },
  { path: ROUTES.INTEGRATION_INBOUND_POLICIES, crumbs: ['OSS Integration', 'Inbound Policies'] },
  { path: ROUTES.INTEGRATION_PARTNERS, crumbs: ['OSS Integration', 'Partners'] },
  { path: ROUTES.INTEGRATION_RUNTIME_MAPPINGS, crumbs: ['OSS Integration', 'Mappings'] },
  { path: ROUTES.INTEGRATION_MAPPINGS, crumbs: ['OSS Integration', 'Mappings'] },
  { path: ROUTES.INTEGRATION_RUNTIME_TEMPLATES, crumbs: ['OSS Integration', 'Templates'] },
  { path: ROUTES.INTEGRATION_TEMPLATES, crumbs: ['OSS Integration', 'Templates'] },
  { path: ROUTES.INTEGRATION_RUNTIME_VALIDATION_RULES, crumbs: ['OSS Integration', 'Validation Rules'] },
  { path: ROUTES.INTEGRATION_VALIDATION_RULES, crumbs: ['OSS Integration', 'Validation Rules'] },
  { path: ROUTES.GESTION_ACCESS_DASHBOARD, crumbs: ['Gestion des Acces', 'Dashboard'] },
  { path: ROUTES.GESTION_ACCESS_REQUESTS, crumbs: ['Gestion des Acces', 'Requests'] },
  { path: ROUTES.GESTION_ACCESS_VALIDATION, crumbs: ['Gestion des Acces', 'Validation'] },
  { path: ROUTES.GESTION_ACCESS_ADMIN, crumbs: ['Gestion des Acces', 'Administration'] },
  { path: ROUTES.USERS, crumbs: ['Users'] },
  { path: ROUTES.USER_NEW, crumbs: ['Users', 'Register Identity'] },
  { path: ROUTES.USER_DETAIL, crumbs: ['Users', 'User Detail'] },
  { path: ROUTES.ORGANIZATIONS, crumbs: ['Organizations'] },
  { path: ROUTES.ORGANIZATION_NEW, crumbs: ['Organizations', 'New Organization'] },
  { path: ROUTES.ORGANIZATION_DETAIL, crumbs: ['Organizations', 'Organization Detail'] },
  { path: ROUTES.AUTHORIZATION, crumbs: ['Authorization'] },
  { path: ROUTES.APPLICATIONS, crumbs: ['Applications'] },
  { path: ROUTES.AUDIT, crumbs: ['Audit'] },
  { path: ROUTES.PROFILE, crumbs: ['Profile'] },
  { path: `${ROUTES.INTEGRATION_BASE}/*`, crumbs: ['OSS Integration'], end: false },
  { path: `${ROUTES.GESTION_ACCESS_BASE}/*`, crumbs: ['Gestion des Acces'], end: false },
  { path: `${ROUTES.DECLARATION_SOMMAIRE_BASE}/*`, crumbs: ['Declaration Sommaire'], end: false },
  { path: `${ROUTES.ENS_BASE}/*`, crumbs: ['Déclarations ENS'], end: false },
  { path: `${ROUTES.MARITIME_BASE}/*`, crumbs: ['Maritime Operations'], end: false },
  { path: `${ROUTES.REFERENTIAL_BASE}/*`, crumbs: ['Referentials'], end: false },
];

// Exported so a test can walk every route the app actually registers and assert none of
// them silently fall through to no breadcrumb - matching order matters (first match wins),
// so a route like /users/new must be listed ahead of the /users/:id detail route.
export function resolveBreadcrumbs(pathname: string): string[] {
  for (const route of BREADCRUMB_ROUTES) {
    if (matchPath({ path: route.path, end: route.end ?? true }, pathname)) {
      return [...route.crumbs];
    }
  }
  return [];
}

function useBreadcrumbs(): string[] {
  const { pathname } = useLocation();
  return useMemo(() => resolveBreadcrumbs(pathname), [pathname]);
}

export function Header() {
  const crumbs = useBreadcrumbs();
  const { user, logout } = useAuth();
  const {
    currentApplication,
    filteredApplications,
    selectApplication,
    classificationOptions,
    shouldShowClassificationBar,
    selectedClassification,
    selectClassification,
  } = useVisibleAppsByClassification();
  const visibleApplications = filteredApplications.length > 0
    ? filteredApplications
    : [{ clientId: currentApplication.clientId, displayName: currentApplication.displayName }];
  const displayName = user?.profile.name ?? user?.profile.preferred_username ?? 'Unknown';
  const displayEmail = user?.profile.email ?? '';
  const avatarLetter = (user?.profile.name ?? user?.profile.email ?? '?').charAt(0).toUpperCase();

  return (
    <div
      className="sticky top-0 z-10 shrink-0 shadow-sm"
      style={{ backgroundColor: 'var(--color-surface-header)' }}
    >
      <header
        className="flex h-16 items-center gap-4 border-b px-5"
        style={{
          backgroundColor: 'var(--color-surface-header)',
          borderColor: 'var(--color-border)',
        }}
      >
        <NavLink to={ROUTES.DASHBOARD} className="flex h-11 w-[132px] shrink-0 items-center no-underline sm:w-[184px]">
          <img
            src={brandLogo}
            alt="OSS NWM Identity Hub"
            className="block h-auto w-full object-contain"
          />
        </NavLink>

        <div className="hidden h-9 w-px bg-gray-200 sm:block" />

        {crumbs.length > 0 ? (
          <nav aria-label="Breadcrumb" className="min-w-0 flex-1 md:flex-initial">
            <ol className="flex items-center gap-1.5 text-sm">
              {crumbs.map((crumb, i) => (
                <li
                  key={i}
                  className={
                    i < crumbs.length - 1
                      ? 'hidden items-center gap-1.5 md:flex'
                      : 'flex min-w-0 items-center gap-1.5'
                  }
                >
                  {i > 0 && (
                    <svg viewBox="0 0 16 16" className="hidden h-3.5 w-3.5 shrink-0 text-gray-300 md:block" fill="currentColor" aria-hidden="true">
                      <path d="M5.293 2.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L8.586 7 5.293 3.707a1 1 0 010-1.414z" />
                    </svg>
                  )}
                  <span
                    className={
                      i === crumbs.length - 1
                        ? 'truncate font-semibold text-gray-900'
                        : 'text-gray-500 hover:text-gray-700'
                    }
                  >
                    {crumb}
                  </span>
                </li>
              ))}
            </ol>
          </nav>
        ) : (
          <span className="text-sm font-semibold text-gray-900">OSS NWM | Identity Hub</span>
        )}

        <div className="flex-1" />

        {visibleApplications.length > 1 ? (
          <label className="flex w-[min(44vw,14rem)] min-w-[9rem] flex-col gap-0.5 md:w-64 lg:min-w-[220px]">
            <span className="hidden text-[10px] font-semibold uppercase tracking-widest text-gray-400 sm:inline">
              Workspace
            </span>
            <span className="relative">
              <select
                value={currentApplication.clientId}
                onChange={(event) => selectApplication(event.currentTarget.value as WorkspaceClientId)}
                className="h-9 w-full appearance-none rounded-md border border-gray-200 bg-white py-1.5 pl-3 pr-9 text-sm font-semibold text-gray-900 shadow-sm transition-colors hover:border-teal-200 focus:border-teal-400 focus:outline-none focus:ring-2 focus:ring-teal-100"
                aria-label="Switch workspace"
              >
                {visibleApplications.map((application) => (
                  <option key={application.clientId} value={application.clientId}>
                    {application.displayName}
                  </option>
                ))}
              </select>
              <svg
                viewBox="0 0 20 20"
                fill="currentColor"
                className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                aria-hidden="true"
              >
                <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
              </svg>
            </span>
          </label>
        ) : (
          <span
            className="hidden rounded px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest md:inline-flex"
            style={{
              backgroundColor: 'var(--nwm-teal-50)',
              color: 'var(--nwm-teal-700)',
              border: '1px solid var(--nwm-teal-100)',
            }}
          >
            {currentApplication.badgeLabel}
          </span>
        )}

        {user ? (
          <NavLink
            to={ROUTES.PROFILE}
            className="flex min-w-0 items-center gap-2 rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-left no-underline shadow-sm transition-colors hover:border-teal-200"
            title="My Profile"
          >
            <span
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-bold text-white"
              style={{ background: 'linear-gradient(135deg, var(--nwm-teal-700), var(--nwm-navy-700))' }}
              aria-hidden="true"
            >
              {avatarLetter}
            </span>
            <span className="hidden min-w-0 flex-col leading-tight sm:flex">
              <span className="truncate text-xs font-semibold text-gray-900">{displayName}</span>
              <span className="max-w-44 truncate text-[11px] text-gray-500">{displayEmail}</span>
            </span>
          </NavLink>
        ) : null}

        {user ? (
          <button
            type="button"
            onClick={() => void logout()}
            className="platform-shell-button flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-gray-200 bg-white p-0 text-gray-500 transition-colors hover:border-red-200 hover:text-red-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500"
            title="Sign out"
            aria-label="Sign out"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.8} stroke="currentColor" className="h-4 w-4" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15M12 9l-3 3m0 0l3 3m-3-3h12.75" />
            </svg>
          </button>
        ) : null}
      </header>

      <ApplicationClassificationBar
        options={classificationOptions}
        selectedClassification={selectedClassification}
        shouldShow={shouldShowClassificationBar}
        onSelect={selectClassification}
      />
    </div>
  );
}
